/* Modulo Video Carousel — multi-card slider with lightbox playback.
   Vanilla, lazy-init, reduced-motion aware. */
(function () {
  'use strict';

  if (typeof window === 'undefined') return;

  var REDUCE = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var SWIPE_THRESHOLD = 40;

  /* ─── URL parsing helpers ─────────────────────────────── */
  function parseVideoUrl(url) {
    if (!url) return null;
    var m;
    // YouTube: youtube.com/watch?v=ID, youtu.be/ID, youtube.com/embed/ID
    m = url.match(/(?:youtube\.com\/(?:watch\?.*v=|embed\/)|youtu\.be\/)([A-Za-z0-9_-]{11})/);
    if (m) return { provider: 'youtube', id: m[1] };
    // Vimeo: vimeo.com/ID, player.vimeo.com/video/ID
    m = url.match(/(?:vimeo\.com\/|player\.vimeo\.com\/video\/)(\d+)/);
    if (m) return { provider: 'vimeo', id: m[1] };
    return null;
  }

  function buildEmbedUrl(parsed) {
    if (!parsed) return '';
    if (parsed.provider === 'youtube') {
      return 'https://www.youtube.com/embed/' + parsed.id + '?autoplay=1&rel=0';
    }
    if (parsed.provider === 'vimeo') {
      return 'https://player.vimeo.com/video/' + parsed.id + '?autoplay=1';
    }
    return '';
  }

  /* ─── Slider helpers ──────────────────────────────────── */
  function getCardsPerView(track) {
    var styles = window.getComputedStyle(track);
    var cpv = parseFloat(styles.getPropertyValue('--modulo-vc-cpv')) || 3;
    var cpvm = parseFloat(styles.getPropertyValue('--modulo-vc-cpv-m')) || 1.2;
    return window.innerWidth <= 860 ? cpvm : cpv;
  }

  function getGap(track) {
    var styles = window.getComputedStyle(track);
    return parseFloat(styles.gap) || 0;
  }

  function maxIndex(total, cpv) {
    return Math.max(0, Math.ceil(total - cpv));
  }

  function clearChildren(node) {
    while (node.firstChild) node.removeChild(node.firstChild);
  }

  /* ─── Init ────────────────────────────────────────────── */
  function initVideoCarousel(slider) {
    var track = slider.querySelector('.modulo-video-carousel__track');
    if (!track) return;

    var cards = Array.prototype.slice.call(track.querySelectorAll('.modulo-video-carousel__card'));
    if (!cards.length) return;

    var prevBtn  = slider.querySelector('[data-modulo-vc-prev]');
    var nextBtn  = slider.querySelector('[data-modulo-vc-next]');
    var dotsHost = slider.querySelector('[data-modulo-vc-dots]');

    var index = 0;
    var cpv = getCardsPerView(track);
    var max = maxIndex(cards.length, cpv);

    function cardStep() {
      var first = cards[0];
      if (!first) return 0;
      return first.getBoundingClientRect().width + getGap(track);
    }

    function update() {
      var offset = -1 * cardStep() * index;
      track.style.transform = 'translate3d(' + offset + 'px, 0, 0)';

      if (prevBtn) prevBtn.disabled = index <= 0;
      if (nextBtn) nextBtn.disabled = index >= max;

      if (dotsHost) {
        var dots = dotsHost.querySelectorAll('button');
        for (var i = 0; i < dots.length; i++) {
          dots[i].setAttribute('aria-selected', i === index ? 'true' : 'false');
        }
      }
    }

    function goTo(i) {
      index = Math.max(0, Math.min(max, i));
      update();
    }

    function next() { goTo(index + 1); }
    function prev() { goTo(index - 1); }

    function buildDots() {
      if (!dotsHost) return;
      clearChildren(dotsHost);
      var pages = max + 1;
      for (var i = 0; i < pages; i++) {
        var btn = document.createElement('button');
        btn.type = 'button';
        btn.setAttribute('role', 'tab');
        btn.setAttribute('aria-label', 'Go to slide ' + (i + 1));
        btn.setAttribute('aria-selected', i === 0 ? 'true' : 'false');
        (function (idx) {
          btn.addEventListener('click', function () { stopAutoplay(); goTo(idx); });
        })(i);
        dotsHost.appendChild(btn);
      }
    }

    /* ─── Autoplay ─── */
    var autoplay = slider.getAttribute('data-autoplay') === '1';
    var interval = parseInt(slider.getAttribute('data-autoplay-interval'), 10) || 5000;
    var autoplayTimer = null;

    function startAutoplay() {
      if (!autoplay || REDUCE) return;
      stopAutoplay();
      autoplayTimer = setInterval(function () {
        if (index >= max) goTo(0);
        else next();
      }, interval);
    }

    function stopAutoplay() {
      if (autoplayTimer) {
        clearInterval(autoplayTimer);
        autoplayTimer = null;
      }
    }

    /* ─── Pointer / touch swipe ─── */
    var pointerStartX = 0;
    var pointerDelta  = 0;
    var dragging      = false;
    var wasDragging   = false;

    function onPointerDown(e) {
      if (e.button !== undefined && e.button !== 0) return;
      dragging = true;
      wasDragging = false;
      pointerStartX = e.clientX || (e.touches && e.touches[0].clientX) || 0;
      pointerDelta = 0;
      track.classList.add('is-dragging');
      stopAutoplay();
    }

    function onPointerMove(e) {
      if (!dragging) return;
      var x = e.clientX || (e.touches && e.touches[0].clientX) || 0;
      pointerDelta = x - pointerStartX;
      var offset = -1 * cardStep() * index + pointerDelta;
      track.style.transform = 'translate3d(' + offset + 'px, 0, 0)';
    }

    function onPointerUp() {
      if (!dragging) return;
      dragging = false;
      track.classList.remove('is-dragging');
      if (Math.abs(pointerDelta) > SWIPE_THRESHOLD) {
        wasDragging = true;
        if (pointerDelta < 0) next();
        else prev();
      } else {
        wasDragging = false;
        update();
      }
      pointerDelta = 0;
    }

    track.addEventListener('pointerdown', onPointerDown);
    track.addEventListener('pointermove', onPointerMove);
    track.addEventListener('pointerup', onPointerUp);
    track.addEventListener('pointercancel', onPointerUp);
    track.addEventListener('pointerleave', function () { if (dragging) onPointerUp(); });

    /* ─── Wire arrows ─── */
    if (prevBtn) prevBtn.addEventListener('click', function () { stopAutoplay(); prev(); });
    if (nextBtn) nextBtn.addEventListener('click', function () { stopAutoplay(); next(); });

    /* ─── Pause autoplay on hover/focus ─── */
    slider.addEventListener('mouseenter', stopAutoplay);
    slider.addEventListener('mouseleave', startAutoplay);
    slider.addEventListener('focusin',  stopAutoplay);
    slider.addEventListener('focusout', startAutoplay);

    /* ─── Resize handling ─── */
    var resizeTimer = null;
    function onResize() {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(function () {
        cpv = getCardsPerView(track);
        max = maxIndex(cards.length, cpv);
        if (index > max) index = max;
        buildDots();
        update();
      }, 100);
    }
    window.addEventListener('resize', onResize);

    /* ─── Lightbox ─── */
    var section = slider.closest('.modulo-video-carousel');
    var lightbox = section.querySelector('[data-modulo-vc-lightbox]');
    var lightboxBody = section.querySelector('[data-modulo-vc-lightbox-body]');
    var lightboxClose = section.querySelector('[data-modulo-vc-lightbox-close]');

    function openLightbox(videoUrl) {
      var parsed = parseVideoUrl(videoUrl);
      if (!parsed || !parsed.id) return;
      var embedUrl = buildEmbedUrl(parsed);
      if (!embedUrl) return;

      var iframe = document.createElement('iframe');
      iframe.src = embedUrl;
      iframe.setAttribute('allow', 'autoplay; fullscreen');
      iframe.setAttribute('allowfullscreen', '');
      iframe.setAttribute('title', 'Video player');

      clearChildren(lightboxBody);
      lightboxBody.appendChild(iframe);
      lightbox.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
      lightbox._prevFocus = document.activeElement;
      lightboxClose.focus();
    }

    function closeLightbox() {
      lightbox.setAttribute('aria-hidden', 'true');
      clearChildren(lightboxBody);
      document.body.style.overflow = '';
      if (lightbox._prevFocus) lightbox._prevFocus.focus();
    }

    /* Card click → open lightbox (suppressed after swipe) */
    for (var i = 0; i < cards.length; i++) {
      (function (card) {
        card.addEventListener('click', function () {
          if (wasDragging) {
            wasDragging = false;
            return;
          }
          var url = card.getAttribute('data-video-url');
          if (url) openLightbox(url);
        });
      })(cards[i]);
    }

    /* Close handlers */
    if (lightboxClose) {
      lightboxClose.addEventListener('click', closeLightbox);
    }
    if (lightbox) {
      lightbox.addEventListener('click', function (e) {
        if (e.target === lightbox) closeLightbox();
      });
    }
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && lightbox && lightbox.getAttribute('aria-hidden') === 'false') {
        closeLightbox();
      }
    });

    /* ─── Boot ─── */
    buildDots();
    update();
    startAutoplay();
  }

  /* ─── Bootstrap ───────────────────────────────────────── */
  function bootstrap() {
    if (window.moduloLazyInit) {
      window.moduloLazyInit('[data-modulo-vc-slider]', initVideoCarousel);
    } else {
      var sliders = document.querySelectorAll('[data-modulo-vc-slider]');
      for (var i = 0; i < sliders.length; i++) initVideoCarousel(sliders[i]);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootstrap);
  } else {
    bootstrap();
  }
})();
